import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import { fetchAgentStats } from "../../store/agentSlice";
import Sidebar from "../../components/Sidebar";
import PageLoader from "../../components/PageLoader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../Dashboard.scss";
import styles from "./onboarding.module.scss";

export default function Onboarding(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { statsData, statsLoading, statsError } = useSelector(
    (state: RootState) => state.agents,
  );
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  // Fetch agent statistics on component mount
  useEffect(() => {
    dispatch(fetchAgentStats());
  }, [dispatch]);

  // Transform API data to chart format
  const onboardingData = useMemo(() => {
    if (
      !statsData ||
      !statsData.category_stats ||
      statsData.category_stats.length === 0
    )
      return [];
    return statsData.category_stats.map((stat) => ({
      name: stat.category,
      users: stat.user_count,
    }));
  }, [statsData]);

  const totalOnboarded = statsData?.total_user_count ?? 0;

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Show full-page loader while data is being fetched
  if (statsLoading) {
    return (
      <PageLoader label="Loading agent statistics..." activeNav="analytics" />
    );
  }

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="analytics"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <h1 className="page-title">Onboarding</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            <div className={styles.headerSection}>
              <h2 className={styles.totalHeader}>
                Total Accellor users onboarded : {totalOnboarded}
              </h2>
            </div>

            <section className={styles.chartSection}>
              <h2 className={styles.chartTitle}>
                Number of Accellor users onboarded by department
              </h2>
              {statsError ? (
                <div className={styles.errorState}>
                  <p>
                    Unable to load agent statistics. Please try again later.
                  </p>
                </div>
              ) : onboardingData.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No onboarding data available at this time.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={onboardingData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 13 }}
                    />
                    <YAxis />
                    <Tooltip
                      contentStyle={{ fontSize: 14 }}
                      cursor={{ fill: "rgba(0, 210, 170, 0.1)" }}
                    />
                    <Legend />
                    <Bar dataKey="users" fill="#08ADC2" name="# of users" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
